import { useEffect, useState } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { ErrorState } from '@/components/common/ErrorState'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import {
  canAutoReloadForChunk,
  isChunkLoadError,
  recoverFromStaleChunk,
  reloadLatestVersion,
} from '@/utils/chunk-recovery'

function resolveMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return error.statusText || `Error ${error.status}`
  }
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'The page could not be loaded.'
}

export default function RouteErrorPage() {
  const error = useRouteError()
  const chunkError = isChunkLoadError(error)
  // Decided once per error: the effect below uses up the auto reload.
  const [reloading] = useState(() => chunkError && canAutoReloadForChunk())

  useEffect(() => {
    recoverFromStaleChunk(error)
  }, [error])

  if (reloading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-3 text-center">
          <PageSkeleton />
          <p className="text-sm text-muted-foreground">Loading the latest version…</p>
        </div>
      </div>
    )
  }

  if (chunkError) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <ErrorState
          title="Page didn't load"
          description="Check your connection, then reload."
          code="NETWORK"
          actionLabel="Reload"
          onAction={reloadLatestVersion}
          className="w-full max-w-md"
        />
      </div>
    )
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6">
      <ErrorState
        title="Something went wrong"
        description={resolveMessage(error)}
        actionLabel="Reload page"
        onAction={() => window.location.reload()}
        className="w-full max-w-md"
      />
    </div>
  )
}
